import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/Auth";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

interface OrgMember {
  user_id: string;
  member_role: string;
  email: string | null;
  display_name: string | null;
  created_at: string;
}

interface Assessment {
  id: string;
  questionnaire_id: string;
  status: string;
  created_at: string;
}

interface PortfolioUsersPanelProps {
  portfolioId: string;
  portfolioName?: string;
  category?: string | null;
}

type RoleType = "respondent" | "reviewer";

export function PortfolioUsersPanel({ portfolioId, portfolioName, category }: PortfolioUsersPanelProps) {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const { tier } = useAuth();
  const { role: effectiveRole } = useEffectiveRole();
  const isTier1PM = effectiveRole === "portfolio_manager" && tier === "tier_1";
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalRole, setModalRole] = useState<RoleType>("respondent");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>("");
  const [creating, setCreating] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    // Use secure RPC that fetches email/display_name from auth.users & profiles
    const { data, error } = await supabase.rpc("list_portfolio_users", {
      p_portfolio_id: portfolioId,
    });

    if (error) {
      console.error("Error fetching portfolio users:", error);
      toast.error("Failed to load users");
      setLoading(false);
      return;
    }

    // Map RPC result to OrgMember shape
    const enrichedMembers: OrgMember[] = (data || []).map((row: any) => ({
      user_id: row.user_id,
      member_role: row.member_role,
      email: row.email,
      display_name: row.display_name,
      created_at: row.created_at,
    }));

    setMembers(enrichedMembers);
    setLoading(false);
  };

  const fetchAssessments = async () => {
    // Check if admin to use RPC path
    const { data: isAdminRow } = await (supabase.rpc as any)("current_user_is_admin");
    const isAdmin = !!isAdminRow;
    
    if (isAdmin) {
      console.log("[UsersTab] admin assessments via RPC: admin_list_assessments_for_org");
      const { data, error } = await (supabase.rpc as any)("admin_list_assessments_for_org", {
        p_org_id: portfolioId,
      });
      
      if (error) {
        console.error("Error fetching assessments via RPC:", error);
      } else {
        // Map RPC result to expected shape
        setAssessments((data || []).map((a: any) => ({
          id: a.id,
          questionnaire_id: a.questionnaire_id,
          status: a.status,
          created_at: a.created_at,
        })));
      }
    } else {
      // Non-admin (PM): use pm_list_assessments_for_portfolio RPC
      const { data, error } = await (supabase.rpc as any)(
        "pm_list_assessments_for_portfolio",
        { p_portfolio_id: portfolioId }
      );

      if (error) {
        console.error("Error fetching assessments via PM RPC:", error);
      } else {
        setAssessments((data || []).map((a: any) => ({
          id: a.id,
          questionnaire_id: a.questionnaire_id,
          status: a.status,
          created_at: a.created_at,
        })));
      }
    }
  };

  useEffect(() => {
    if (portfolioId) {
      setLoading(true);
      Promise.all([fetchMembers(), fetchAssessments()]).finally(() => {
        setLoading(false);
      });
    }
  }, [portfolioId]);

  const openAddModal = (role: RoleType) => {
    setModalRole(role);
    setEmail("");
    setDisplayName("");
    setTempPassword("");
    // Default to the newest assessment (first in list)
    setSelectedAssessmentId(assessments.length > 0 ? assessments[0].id : "");
    setShowModal(true);
  };

  const handleCreateUser = async () => {
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!tempPassword.trim()) {
      toast.error("Password is required");
      return;
    }
    if (tempPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      setCreating(true);

      const payload = {
        portfolio_id: portfolioId,
        email: email.trim().toLowerCase(),
        password: tempPassword,
        display_name: displayName?.trim() || null,
        member_role: modalRole || "respondent",
      };
      console.log("[create-user payload]", payload);

      if (!payload.member_role) {
        toast.error("Role is required");
        setCreating(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-user", {
        body: payload,
      });

      console.log("[PM Users] edge function result:", { data, error });

      if (error) {
        throw new Error(error.message ?? "Edge function error");
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      toast.success(
        `${modalRole === "respondent" ? "Respondent" : "Reviewer"} created: ${email.trim()} — Password set (testing mode)`
      );

      // refresh list / close modal
      setShowModal(false);
      setEmail("");
      setDisplayName("");
      setTempPassword("");
      setSelectedAssessmentId("");
      fetchMembers();
    } catch (e: any) {
      console.error("[PM Users] RPC error:", e);
      toast.error(e?.message ?? "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const getAssessmentLabel = (assessment: Assessment) => {
    // Prefer portfolio_name as primary label
    if (portfolioName) return portfolioName;
    const titleMap: Record<string, string> = {
      telecom_v1: "Telecom Full Assessment (NIST CSF v2)",
      telecom_v2: "Telecom CSF 2.0 Assessment",
      telecom_self_v1: "Telecom Self-Assessment (NIST CSF – Light)",
      datacenter_v1: "Data Center Assessment",
    };
    return titleMap[assessment.questionnaire_id] || "Questionnaire";
  };

  const respondents = members.filter((m) => m.member_role === "respondent");
  const reviewers = members.filter((m) => m.member_role === "reviewer");

  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        <Button onClick={() => openAddModal("respondent")}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Respondent
        </Button>
        {!isTier1PM && (
          <Button variant="outline" onClick={() => openAddModal("reviewer")}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Reviewer
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Respondents */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Respondents ({respondents.length})</h3>
            {respondents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No respondents assigned</p>
            ) : (
              <ul className="space-y-2">
                {respondents.map((m) => (
                  <li key={m.user_id} className="text-sm border-b pb-2 last:border-0">
                    <div className="font-medium">{m.email || m.display_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      Added: {new Date(m.created_at).toLocaleDateString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Reviewers */}
          {!isTier1PM && (
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Reviewers ({reviewers.length})</h3>
              {reviewers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No reviewers assigned</p>
              ) : (
                <ul className="space-y-2">
                  {reviewers.map((m) => (
                    <li key={m.user_id} className="text-sm border-b pb-2 last:border-0">
                      <div className="font-medium">{m.email || m.display_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        Added: {new Date(m.created_at).toLocaleDateString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Create User Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add {modalRole === "respondent" ? "Respondent" : "Reviewer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tempPassword">Password *</Label>
              <Input
                id="tempPassword"
                type="password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Min 6 characters"
              />
              <p className="text-xs text-muted-foreground">
                Required — user can log in immediately with this password
              </p>
            </div>
            {/* Assessment dropdown - only show if more than 1 assessment */}
            {assessments.length > 1 && (
              <div className="space-y-2">
                <Label>Assign to Assessment</Label>
                <Select
                  value={selectedAssessmentId}
                  onValueChange={setSelectedAssessmentId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assessment" />
                  </SelectTrigger>
                  <SelectContent>
                    {assessments.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {getAssessmentLabel(a)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {assessments.length === 1 && (
              <p className="text-xs text-muted-foreground">
                Will be assigned to: {getAssessmentLabel(assessments[0])}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={creating || !email.trim() || !tempPassword.trim()}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
